import React, { ReactNode, useRef, useState, useEffect } from 'react';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import styled from 'styled-components/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm } from '@fortawesome/pro-solid-svg-icons';
import { faArrowCircleDown } from '@fortawesome/pro-light-svg-icons';
import { faCameraRetro } from '@fortawesome/pro-solid-svg-icons';
import { useSelector } from 'src/client/redux';
import { useFeatureFlags } from 'src/client/helpers/useFeatureFlags';
import ReCAPTCHA from 'react-google-recaptcha';

export type Download = {
  id: string;
  size?: string;
  link: string;
  title: string;
  isDownloadMedia?: boolean;
};

type Props = {
  label: ReactNode;
  downloads?: Download[];
  iconType: 'photo' | 'movie';
  className?: string;
  onItemClick?: (link: string) => void;
};

// convert "280.9 MB" to "281 MB"
const sanitizeSize = (rawSize: string) =>
  rawSize.replace(/(\d+\.?\d*)/, (match) => Math.round(parseFloat(match)).toString());

const DownloadDropdown = (props: Props) => {
  const { label, downloads = [], iconType, className, onItemClick } = props;
  const disabled = !downloads.length;

  const [reCaptchaToken, setReCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const config = useSelector((state) => state.app.config);
  const { enableReCaptchaDownload } = useFeatureFlags();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  if (disabled) return null;

  const generateToken = async () => {
    if (enableReCaptchaDownload) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setIsLoading(true);
      const reCaptchaToken = await reCaptchaRef.current?.executeAsync();
      setReCaptchaToken(reCaptchaToken);
      setIsLoading(false);

      timerRef.current = window.setTimeout(() => {
        setReCaptchaToken(null);
      }, 2 * 60 * 1000);
    }
  };

  const handleDropDownClick = () => {
    if (!reCaptchaToken) {
      generateToken();
    }
  };

  const handleOnContextMenuChange = (e) => {
    if (isLoading) {
      e.preventDefault();
    } else {
      generateToken();
    }
  };

  const handleOnItemClick = (e, isDownloadMedia, linkWithReCaptchaToken) => {
    if (isDownloadMedia && onItemClick) {
      e.preventDefault();
      if (!isLoading) {
        onItemClick(linkWithReCaptchaToken);
        generateToken();
      }
    }
  };

  return (
    <DownloadDropdownWrapper className={className}>
      {enableReCaptchaDownload && (
        <ReCAPTCHA ref={reCaptchaRef} sitekey={config.googleReCaptchaEnterprise.key} size="invisible" />
      )}
      <StyledDropdownButton
        title={
          <Label className="man-label">
            <span>{label}</span>
            <DownloadIconGroup>
              <DownloadTypeIcon icon={iconType === 'photo' ? faCameraRetro : faFilm} aria-hidden="true" />
              <DownloadArrowIcon icon={faArrowCircleDown} aria-hidden="true" />
            </DownloadIconGroup>
            <Caret className="caret" />
          </Label>
        }
        onClick={handleDropDownClick}
      >
        {downloads.map((download) => {
          const { id, size, link, title, isDownloadMedia } = download;

          const linkWithReCaptchaToken = `${link}${
            reCaptchaToken && link.startsWith('/api') ? `?recaptchaToken=${reCaptchaToken}` : ''
          }`;

          return (
            <Dropdown.Item
              eventKey={id}
              href={linkWithReCaptchaToken}
              key={id}
              onClick={(e) => handleOnItemClick(e, isDownloadMedia, linkWithReCaptchaToken)}
              onContextMenu={handleOnContextMenuChange}
            >
              {title}
              {size && <span className="pull-right">{sanitizeSize(size)}</span>}
            </Dropdown.Item>
          );
        })}
      </StyledDropdownButton>
    </DownloadDropdownWrapper>
  );
};

const StyledDropdownButton = styled(DropdownButton)`
  display: inline-block;
  border: 0;
  box-shadow: none;
  padding: 0 12px;
  margin-top: 0px;
  cursor: pointer;

  .btn {
    color: ${(props) => props.theme.primary4} !important;
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;

    :focus,
    :hover {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
    }
  }

  .dropdown-menu {
    width: 185px;
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  padding-right: 3px;
  float: left;
  height: 20px;
`;

const Caret = styled.span`
  margin-left: 8px;
`;

const DownloadTypeIcon = styled(FontAwesomeIcon)`
  position: relative;
  font-size: 1.4rem;
  color: ${(props) => props.theme.primary4};
`;

const DownloadArrowIcon = styled(FontAwesomeIcon)`
  z-index: 2;
  position: absolute;
  right: -8px;
  top: 9px;
  font-size: 0.9rem;
  color: ${(props) => props.theme.primary4};
  background-color: ${(props) => props.theme.primary1};
`;

const DownloadIconGroup = styled.div`
  margin-left: 10px;
  position: relative;
  opacity: 0.9;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const DownloadDropdownWrapper = styled.div`
  display: inline-block;

  .grecaptcha-badge {
    z-index: 2001;
    width: 70px !important;
    overflow: hidden !important;
    transition: all 0.3s ease !important;
    left: 4px !important;

    :hover {
      width: 256px !important;
    }
  }

  & + & {
    ${StyledDropdownButton} {
      padding-left: 0;
    }
  }
`;

export default DownloadDropdown;
